import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Timer } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { formatRemaining } from "@/components/AnnouncementBar";

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const BannerTab = () => {
  const [enabled, setEnabled] = useState(false);
  const [text, setText] = useState("");
  const [deadline, setDeadline] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/settings/announcement")
      .then((r) => {
        setEnabled(!!r.data.enabled);
        setText(r.data.text || "");
        setDeadline(toLocalInput(r.data.deadline));
      })
      .catch(() => toast.error("Could not load announcement"))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings/announcement", {
        enabled,
        text,
        deadline: deadline ? new Date(deadline).toISOString() : null,
      });
      setEnabled(data.enabled);
      setText(data.text);
      setDeadline(toLocalInput(data.deadline));
      toast.success("Announcement updated — live on the storefront now");
    } catch (e) {
      toast.error(apiError(e, "Could not save announcement"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <p className="mt-10 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-cream/30">
        Loading…
      </p>
    );
  }

  const previewRemaining = deadline
    ? formatRemaining(new Date(deadline).getTime() - Date.now())
    : null;

  return (
    <div className="mx-auto mt-10 max-w-2xl" data-testid="banner-tab">
      <div className="border border-cream/10 bg-espresso-surface p-6 sm:p-8">
        <div className="flex items-center gap-3">
          <Megaphone size={18} strokeWidth={1.5} className="text-gold" />
          <h2 className="font-serif text-2xl text-cream">Storefront Announcement</h2>
        </div>
        <p className="mt-2 text-sm leading-relaxed text-cream/50">
          A slim gold strip at the very top of your store — perfect for festive sales, launches and
          shipping notes. Visitors can dismiss it; when you change the text, it reappears for
          everyone.
        </p>

        <div className="mt-6 flex items-center justify-between border border-cream/10 px-4 py-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/60">
            Show announcement
          </span>
          <Switch
            data-testid="banner-enabled-toggle"
            checked={enabled}
            onCheckedChange={setEnabled}
          />
        </div>

        <div className="mt-4">
          <textarea
            data-testid="banner-text-input"
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 200))}
            rows={3}
            placeholder="Diwali Edit is live — flat 20% off all gemstone pendants this week"
            className="w-full resize-none border border-cream/15 bg-transparent px-4 py-3 text-sm text-cream outline-none transition-colors placeholder:text-cream/25 focus:border-gold"
          />
          <p className="mt-1 text-right font-mono text-[9px] text-cream/30">{text.length}/200</p>
        </div>

        <div className="mt-2">
          <div className="mb-2 flex items-center gap-2">
            <Timer size={13} className="text-gold-muted" />
            <label
              htmlFor="banner-deadline"
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/60"
            >
              Sale deadline (optional countdown)
            </label>
          </div>
          <div className="flex items-center gap-3">
            <input
              id="banner-deadline"
              data-testid="banner-deadline-input"
              type="datetime-local"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              className="border border-cream/15 bg-transparent px-4 py-2.5 text-sm text-cream outline-none transition-colors [color-scheme:dark] focus:border-gold"
            />
            {deadline && (
              <button
                type="button"
                data-testid="banner-deadline-clear"
                onClick={() => setDeadline("")}
                className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40 transition-colors hover:text-gold"
              >
                Clear
              </button>
            )}
          </div>
          <p className="mt-2 text-xs text-cream/35">
            Set in your local time. A live countdown appears beside the announcement; it hides
            itself once the deadline passes.
          </p>
        </div>

        {text.trim() && (
          <div className="mt-4">
            <p className="mb-2 font-mono text-[9px] uppercase tracking-[0.25em] text-cream/30">
              Live preview
            </p>
            <div
              data-testid="banner-preview"
              className="flex flex-wrap items-center justify-center gap-x-3 gap-y-0.5 bg-gold px-6 py-2.5"
            >
              <span className="h-1 w-1 rotate-45 bg-espresso" />
              <p className="text-center font-mono text-[10px] uppercase tracking-[0.25em] text-espresso">
                {text}
              </p>
              {previewRemaining && (
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-espresso/70">
                  · Ends in {previewRemaining}
                </span>
              )}
              <span className="h-1 w-1 rotate-45 bg-espresso" />
            </div>
          </div>
        )}

        <button
          data-testid="banner-save-button"
          onClick={save}
          disabled={saving}
          className="mt-6 w-full bg-gold py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-espresso transition-all duration-300 hover:bg-gold-accent active:scale-[0.98] disabled:opacity-50"
        >
          {saving ? "Saving…" : "Publish Announcement"}
        </button>
      </div>
    </div>
  );
};

export default BannerTab;
